import {
	featuresFlagsConf,
} from '@appConf/featuresFlags.conf';
import {
	LayerProps,
} from '@appConf/layers.conf';
import {
	RUNTIME_CONFIG,
} from '@appConf/runtime.conf';
import {
	EnumEnvironments,
} from '@enums/environments.enum';
import {
	EnumQueryMethods,
} from '@enums/query.enum';
import {
	TypeConfig,
} from '@modules/config';
import {
	getApiDomainByEnv,
	getEnv,
} from '@modules/domain';

type EnumBreakpoints = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type TypeBreakpointsMap = {
	[key in EnumBreakpoints]: number;
};

// This map defines the breakpoints used for responsive design.
// Ensure to keep it updated with any new design requirements.
// Modifications here may affect layout in various screen sizes.
const breakPointsMap: TypeBreakpointsMap = {
	lg: 1025,
	md: 768,
	sm: 360,
	xl: 1440,
	xs: 0,
};

enum EnumThemes {
	DARK = 'dark',
	LIGHT = 'light',
}

export enum EnumImageFormatQuery {
	CARD = '?tr=w-234,h-210,c-at_max',
	CARD_MOBILE = '?tr=w-329,h-180,c-at_max',
	CARTO = '?tr=w-240,h-158,c-at_max',
	SLIDER = '?tr=w-533,h-376,c-at_max',
	SLIDER_MOBILE = '?tr=w-428,h-286,c-at_max',
	SLIDER_MODAL = '?tr=w-810,h-514,c-at_max',
	SLIDER_MODAL_MOBILE = '?tr=w-390,h-390,c-at_max',
	SPECIFICATION = '?tr=w-77,h-64,c-fill',
	SPECIFICATION_MOBILE = '?tr=w-330,h-140,c-fill',
	SQUARE = '?tr=w-40,h-40',
	SQUARE_PROFILE = '?tr=w-40,h-40,c-at_min',
	TABLE_VIEW = '?tr=w-88,h-64,c-fill',
	USERCARD = '?tr=w-40,h-40,fo-auto',
}

interface InterfaceGlobalsApiConf {
	communes: string;
	demand: {
		offers: {
			limit: number;
		};
	};
}

interface InterfaceAutosuggestConf {
	maxNbResults: number;
	minNbInputChars: number;
}

export interface InterfaceHostsConf {
	[key: string]: {
		[key in EnumEnvironments]?: string;
	};
}

interface InterfaceLanguageConf {
	name: string;
	code: string;
	flag: string;
}

interface InterfaceLevenshteinConf {
	ignoreLength: number;
	long: {
		minLength: number;
		distance: number;
	};
	small: {
		minLength: number;
		distance: number;
	};
}

interface InterfaceMapboxConf {
	bounds: {
		maxZoom: {
			mobile: number;
			desktop: number;
		};
		padding: {
			mobile: number;
			desktop: number;
		};
	};
	config: {
		accessToken: string;
		style: string;
		coordinates: number[]; // PARIS
		zoom: number;
	};
	layers?: LayerProps;
}

interface InterfaceTimeoutConf {
	debounce: number;
	mouseleave: number;
	disabled: number;
	hide: number;
	reload_session: number;
	resize: number;
	scroll: number;
	toast: number;
}

interface InterfaceHasCRMConf {
	isEnabled: boolean;
	name: string;
	authorized_company_ids: number[];
	authorized_usernames: string[];
}

export interface InterfaceAppConfVars {
	api: InterfaceGlobalsApiConf;
	api_service_lpdi_url: string;
	autosuggest: InterfaceAutosuggestConf;
	breakpoints: TypeBreakpointsMap;
	config: () => TypeConfig;
	env: () => EnumEnvironments;
	featureFlags: {
		[key: string]: InterfaceHasCRMConf;
	};
	language_default: InterfaceLanguageConf;
	language_list: InterfaceLanguageConf[];
	levenshtein: InterfaceLevenshteinConf;
	publicPath: string;
	privatePath: string;
	mapbox: InterfaceMapboxConf;
	request: {
		default: {
			method: EnumQueryMethods;
			headers: {
				'Content-type': string;
				'X-Requested-With': string;
			};
			credentials?: RequestCredentials;
		};
	};
	sfLegacyPath: string;
	sfLegacyPathPROD: string;
	sfPath: string;
	sfPathApryse: string;
	sfPathMarketerLogo: string;
	sfPathMesOffres: string;
	sfPathMesOffresDomain?: string;
	sfPathPROD: string;
	theme_default: string;
	theme_list: EnumThemes[];
	timeout: InterfaceTimeoutConf;
	tools: {
		beamer?: {
			product_id: string;
		};
		google?: {
			tag_manager?: {
				key?: string;
			};
		};
	};
	router: {
		[key: string]: {
			basename: string;
		};
	};
	session: {
		lifetime: number;
	};
}

export const APP_CONF_VARS: InterfaceAppConfVars = {
	api: {
		communes: 'https://geo.api.gouv.fr/communes?format=json&fields=nom,code,centre,population',
		demand: {
			offers: {
				limit: 1500,
			},
		},
	},
	api_service_lpdi_url: getEnv() === 'prod' ? 'https://api.laplacedelimmobilier-pro.com' : `https://api.lpdi-${getEnv()}.com`,
	autosuggest: {
		maxNbResults: 8,
		minNbInputChars: 3,
	},
	breakpoints: breakPointsMap,
	config: (): TypeConfig => window.STONAL_CONFIG,
	env: (): EnumEnvironments => window.STONAL_CONFIG.STONAL_ENV as EnumEnvironments,
	featureFlags: featuresFlagsConf,
	language_default: {
		code: 'fr-FR',
		flag: 'fr',
		name: 'french',
	},
	language_list: [
		{
			code: 'fr-FR',
			flag: 'fr',
			name: 'french',
		},
		{
			code: 'en-GB',
			flag: 'gb',
			name: 'english UK',
		},
	],
	levenshtein: {
		ignoreLength: 2,
		long: {
			distance: 2,
			minLength: 8,
		},
		small: {
			distance: 1,
			minLength: 0,
		},
	},
	mapbox: {
		bounds: {
			maxZoom: {
				desktop: 20,
				mobile: 10,
			},
			padding: {
				desktop: 200,
				mobile: 50,
			},
		},
		config: {
			accessToken: 'pk.eyJ1IjoiZWRkaGJzIiwiYSI6ImNqd20zcW4yZjAxaHk0YXBmZGtucmdhdjQifQ.EW7D17No8Vhy7NrviHUuXg',
			coordinates: [
				2.352894817894053,
				48.85635302576033,
			], // PARIS
			style: 'mapbox://styles/eddhbs/cl3ed4dp8004u14ndh2w6e6xz',
			zoom: 10,
		},
		layers: {
			bbox: '{bbox-epsg-3857}',
			key: '3c2b7cf230387dabacaa56d72d74024a',
			srs: 'EPSG%3A900913',
			tileSize: 256,
		},
	},
	privatePath: '/app/api',
	publicPath: '/webapp',

	request: {
		default: {
			credentials: 'include',
			headers: {
				'Content-type': 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
			},
			method: EnumQueryMethods.GET,
		},
	},
	router: {
		myoffers: {
			basename: 'app/de/',
		},
		webapp: {
			basename: 'app/webapp/',
		},
	},
	session: {
		lifetime: 1440000, // same as framework.sessions.gc_maxlifetime in folder LPdI/config/packages/framework.yaml
	},
	sfLegacyPath: RUNTIME_CONFIG.IS_LEGACY_LOCAL_ENV === 'true' ? 'http://localhost:8080/LPdI/LPdI_dev.php/fr_FR' : `${getApiDomainByEnv()}/LPdI/fr_FR`,
	sfLegacyPathPROD: '/LPdI/fr_FR/',
	sfPath: RUNTIME_CONFIG.IS_LEGACY_LOCAL_ENV === 'true' ? 'http://localhost:8080/app' : `${getApiDomainByEnv()}/app`,
	sfPathApryse: RUNTIME_CONFIG.CDN_BASE_PATH
		? `${RUNTIME_CONFIG.CDN_BASE_PATH}webapp/webviewer/lib`
		: '/webapp/webviewer/lib',
	sfPathMarketerLogo: '/app/marketer/logo/',
	sfPathMesOffres: '/de',
	sfPathMesOffresDomain: RUNTIME_CONFIG.IS_LEGACY_LOCAL_ENV === 'true' ? 'http://localhost:5174' : undefined,
	sfPathPROD: '/app/',
	theme_default: 'light',
	theme_list: Object.values(EnumThemes),
	timeout: {
		debounce: 500,
		disabled: 20000,
		hide: 500,
		mouseleave: 250,
		reload_session: 600000,
		resize: 250,
		scroll: 1000,
		toast: 5000,
	},
	tools: {
		beamer: {
			product_id: 'JjXJhZqd31237',
		},
	},
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [
	10,
	20,
	50,
	100,
];